/**
 * Colours and sizes for the graph, per node type, in the app's light and dark
 * palettes (the surfaces match Calendar.css: #ffffff / #111113). Kept in one
 * place so the canvas, the legend and the details panel agree.
 */

export const TYPE_STYLE = Object.freeze({
  project: { light: '#8869eb', dark: '#a48bf5', size: 11, labelKey: 'graph.type.project' },
  collection: { light: '#f59e0b', dark: '#fbbf24', size: 8, labelKey: 'graph.type.collection' },
  folder: { light: '#d6a64f', dark: '#c79a4a', size: 5, labelKey: 'graph.type.folder' },
  request: { light: '#10b981', dark: '#34d399', size: 4.5, labelKey: 'graph.type.request' },
  environment: { light: '#06b6d4', dark: '#22d3ee', size: 7, labelKey: 'graph.type.environment' },
  board: { light: '#3b82f6', dark: '#60a5fa', size: 8, labelKey: 'graph.type.board' },
  task: { light: '#6b8fd6', dark: '#8fb0f0', size: 4.5, labelKey: 'graph.type.task' },
  doc_folder: { light: '#a8a29e', dark: '#8a8580', size: 5, labelKey: 'graph.type.doc_folder' },
  document: { light: '#ec4899', dark: '#f472b6', size: 5.5, labelKey: 'graph.type.document' },
  doc_workspace: { light: '#db2777', dark: '#f9a8d4', size: 9, labelKey: 'graph.type.doc_workspace' },
  appointment: { light: '#f97316', dark: '#fb923c', size: 5, labelKey: 'graph.type.appointment' },
  db_connection: { light: '#ef4444', dark: '#f87171', size: 7, labelKey: 'graph.type.db_connection' },
  ssh_host: { light: '#64748b', dark: '#94a3b8', size: 6, labelKey: 'graph.type.ssh_host' },
  git_repo: { light: '#0f766e', dark: '#2dd4bf', size: 7, labelKey: 'graph.type.git_repo' },
});

export const FALLBACK_STYLE = { light: '#94a3b8', dark: '#94a3b8', size: 5 };

/** Solid colour for a node type (used for dots, icon badges and legend chips). */
export function typeColor(type, dark) {
  const s = TYPE_STYLE[type] || FALLBACK_STYLE;
  return dark ? s.dark : s.light;
}

export function isDarkTheme() {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
}

export function accentHex() {
  if (typeof document === 'undefined') return '#8869eb';
  const rgb = getComputedStyle(document.documentElement).getPropertyValue('--accent-rgb').trim();
  const parts = rgb.split(',').map((p) => Number(p.trim()));
  if (parts.length !== 3 || parts.some((p) => !Number.isFinite(p))) return '#8869eb';
  return `#${parts.map((p) => Math.round(p).toString(16).padStart(2, '0')).join('')}`;
}

export function nodeColor(node, dark) {
  if (node.type === 'project' && node.color) return node.color;
  const s = TYPE_STYLE[node.type] || FALLBACK_STYLE;
  return dark ? s.dark : s.light;
}

/** World-space radius: type base, grown gently by how connected the node is. */
export function nodeRadius(node, degree) {
  const s = TYPE_STYLE[node.type] || FALLBACK_STYLE;
  return s.size + Math.min(10, Math.sqrt(degree || 0) * 1.3);
}
